import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import blogApi from '@/api/blog';
import type { PublicCommentDto, CreateCommentRequest } from '@/types';
import { formatDate } from '@/lib/utils';
import { useLanguage } from '@/context/LanguageContext';

interface CommentSectionProps {
  postSlug: string;
}

const CommentSection: React.FC<CommentSectionProps> = ({ postSlug }) => {
  const { language } = useLanguage();
  const queryClient = useQueryClient();

  const { data: comments = [], isLoading } = useQuery({
    queryKey: ['blog', 'post', postSlug, 'comments'],
    queryFn: () => blogApi.getComments(postSlug),
  });

  if (isLoading) {
    return (
      <div className="mt-12">
        <h2 className="text-2xl font-bold mb-6">Comments</h2>
        <p className="text-gray-500">Loading comments...</p>
      </div>
    );
  }

  return (
    <div className="mt-12">
      <h2 className="text-2xl font-bold mb-6">
        Comments {comments.length > 0 && `(${comments.length})`}
      </h2>

      <CommentForm postSlug={postSlug} onSuccess={() => {
        queryClient.invalidateQueries({ queryKey: ['blog', 'post', postSlug, 'comments'] });
      }} />

      {comments.length === 0 ? (
        <p className="text-gray-500 mt-6">No comments yet. Be the first to comment!</p>
      ) : (
        <div className="mt-8 space-y-6">
          {comments.map((comment) => (
            <CommentItem
              key={comment.id}
              comment={comment}
              postSlug={postSlug}
              language={language}
            />
          ))}
        </div>
      )}
    </div>
  );
};

// ─── Comment Form ───

interface CommentFormProps {
  postSlug: string;
  parentCommentId?: number;
  onSuccess: () => void;
  onCancel?: () => void;
}

const CommentForm: React.FC<CommentFormProps> = ({ postSlug, parentCommentId, onSuccess, onCancel }) => {
  const { register, handleSubmit, reset, formState: { errors } } = useForm<CreateCommentRequest>();

  const mutation = useMutation({
    mutationFn: (data: CreateCommentRequest) =>
      blogApi.submitComment(postSlug, { ...data, parentCommentId }),
    onSuccess: () => {
      reset();
      onSuccess();
    },
  });

  return (
    <form onSubmit={handleSubmit((data) => mutation.mutate(data))} className="space-y-4">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <input
            {...register('guestName', { required: 'Name is required' })}
            placeholder="Your name *"
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none"
          />
          {errors.guestName && (
            <p className="text-red-500 text-xs mt-1">{errors.guestName.message}</p>
          )}
        </div>
        <div>
          <input
            {...register('guestEmail', {
              required: 'Email is required',
              pattern: { value: /^\S+@\S+$/i, message: 'Invalid email' },
            })}
            placeholder="Your email *"
            type="email"
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none"
          />
          {errors.guestEmail && (
            <p className="text-red-500 text-xs mt-1">{errors.guestEmail.message}</p>
          )}
        </div>
      </div>

      <div>
        <input
          {...register('guestWebsite')}
          placeholder="Website (optional)"
          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none"
        />
      </div>

      <div>
        <textarea
          {...register('content', { required: 'Comment is required' })}
          placeholder={parentCommentId ? 'Write a reply...' : 'Write a comment...'}
          rows={4}
          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none resize-y"
        />
        {errors.content && (
          <p className="text-red-500 text-xs mt-1">{errors.content.message}</p>
        )}
      </div>

      <div className="flex gap-3">
        <button
          type="submit"
          disabled={mutation.isPending}
          className="btn btn-primary btn-sm"
        >
          {mutation.isPending ? 'Submitting...' : parentCommentId ? 'Reply' : 'Post Comment'}
        </button>
        {onCancel && (
          <button type="button" onClick={onCancel} className="btn btn-sm border border-gray-300">
            Cancel
          </button>
        )}
      </div>

      {mutation.isSuccess && (
        <p className="text-green-600 text-sm">Comment submitted for review. Thank you!</p>
      )}
      {mutation.isError && (
        <p className="text-red-500 text-sm">Failed to submit comment. Please try again.</p>
      )}
    </form>
  );
};

// ─── Comment Item ───

interface CommentItemProps {
  comment: PublicCommentDto;
  postSlug: string;
  language: string;
  depth?: number;
}

const CommentItem: React.FC<CommentItemProps> = ({ comment, postSlug, language, depth = 0 }) => {
  const [showReply, setShowReply] = useState(false);
  const queryClient = useQueryClient();

  return (
    <div className={depth > 0 ? 'ml-8 pl-4 border-l-2 border-gray-100' : ''}>
      <div className="flex gap-3">
        {comment.avatarUrl ? (
          <img
            src={comment.avatarUrl}
            alt={comment.authorDisplayName}
            className="w-10 h-10 rounded-full object-cover flex-shrink-0"
          />
        ) : (
          <div className="w-10 h-10 rounded-full bg-primary/10 text-primary flex items-center justify-center font-semibold text-sm flex-shrink-0">
            {comment.authorDisplayName.charAt(0).toUpperCase()}
          </div>
        )}

        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <span className="font-medium text-sm">{comment.authorDisplayName}</span>
            <span className="text-xs text-gray-400">
              {formatDate(comment.createdOn, language)}
            </span>
          </div>

          <p className="text-gray-700 text-sm whitespace-pre-wrap">{comment.content}</p>

          {depth < 3 && (
            <button
              onClick={() => setShowReply(!showReply)}
              className="text-xs text-primary hover:underline mt-2"
            >
              {showReply ? 'Cancel' : 'Reply'}
            </button>
          )}

          {showReply && (
            <div className="mt-3">
              <CommentForm
                postSlug={postSlug}
                parentCommentId={comment.id}
                onSuccess={() => {
                  setShowReply(false);
                  queryClient.invalidateQueries({ queryKey: ['blog', 'post', postSlug, 'comments'] });
                }}
                onCancel={() => setShowReply(false)}
              />
            </div>
          )}
        </div>
      </div>

      {/* Nested replies */}
      {comment.replies && comment.replies.length > 0 && (
        <div className="mt-4 space-y-4">
          {comment.replies.map((reply) => (
            <CommentItem
              key={reply.id}
              comment={reply}
              postSlug={postSlug}
              language={language}
              depth={depth + 1}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default CommentSection;
